import { useCallback, useEffect, useState } from "react";
import API from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Bank, ArrowsClockwise } from "@phosphor-icons/react";

const LABELS = { pending_customer_approval: "awaiting client approval", pending_submission: "scheduled", submitted: "submitted to the bank", confirmed: "confirmed", paid_out: "paid out",
  failed: "failed", cancelled: "cancelled", charged_back: "charged back", customer_approval_denied: "declined by client" };

/** "Collect by Direct Debit" for an invoice whose client has a GoCardless mandate. Renders nothing when DD isn't available. */
export default function InvoiceDirectDebitButton({ invoice, onChanged }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const clientId = invoice?.client_id;

  const load = useCallback(async () => {
    if (!clientId) return;
    try { const { data } = await API.get(`/integrations/gocardless/client/${clientId}`); setState(data); }
    catch { setState(null); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const gc = invoice?.gocardless;
  const inFlight = gc?.payment_id && !["failed", "cancelled", "charged_back", "customer_approval_denied"].includes(gc.status);

  const collect = async () => {
    if (!confirming) {
      setConfirming(true);
      toast(`Click again to collect £${Number(invoice.total_inc_vat || invoice.grand_total || invoice.total || 0).toFixed(2)} from ${invoice.client_name || "the client"} by Direct Debit`);
      setTimeout(() => setConfirming(false), 8000);
      return;
    }
    setConfirming(false);
    setBusy(true);
    try {
      const { data } = await API.post(`/invoices/${invoice.id}/collect-dd`);
      toast.success(`Direct Debit requested — charge date ${data.charge_date}`);
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not request the Direct Debit"); }
    finally { setBusy(false); }
  };

  const refresh = async () => {
    setBusy(true);
    try { const { data } = await API.post(`/invoices/${invoice.id}/collect-dd/refresh`); toast.success(`Direct Debit is ${LABELS[data.status] || data.status}`); onChanged && onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); }
    finally { setBusy(false); }
  };

  if (!state?.connected) return null;
  if (inFlight) {
    return (
      <span className="inline-flex items-center gap-1 text-xs rounded-md border border-indigo-200 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 px-2 py-1" data-testid="invoice-dd-status">
        <Bank size={13} /> Direct Debit {LABELS[gc.status] || gc.status}{gc.charge_date ? ` · ${gc.charge_date}` : ""}
        <button type="button" onClick={refresh} disabled={busy} className="ml-1" title="Refresh from GoCardless" data-testid="invoice-dd-refresh-btn"><ArrowsClockwise size={12} className={busy ? "animate-spin" : ""} /></button>
      </span>
    );
  }
  if (!state.active || ["paid", "cancelled"].includes(invoice?.status)) return null;
  return (
    <Button size="sm" variant="outline" className="text-indigo-700 border-indigo-300 hover:bg-indigo-50 dark:text-indigo-300" onClick={collect} disabled={busy} data-testid="invoice-collect-dd-btn" title="Collect this invoice from the client's Direct Debit mandate">
      <Bank size={14} className="mr-1" /> {busy ? "Requesting…" : confirming ? "Confirm Direct Debit" : "Collect by Direct Debit"}
    </Button>
  );
}
